<template>
    <app-template>
        <div class="p-6 max-w-7xl mx-auto" v-if="!fetching">

            <!-- Header -->
            <div class="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
                <div>
                    <h1 class="text-3xl font-black text-black uppercase tracking-tight">
                        Configuration <span class="text-orange-500">Réservation</span>
                    </h1>
                    <p class="text-gray-500 text-sm mt-1">Personnalisez l'interface de prise de rendez-vous client.</p>
                </div>
                <div class="flex items-center gap-6">
                    <div class="flex items-center gap-3 bg-gray-100 px-4 py-2 rounded-full">
                        <span class="text-xs font-bold uppercase tracking-wider text-gray-600">
                            {{ form.actif ? 'En ligne' : 'Hors ligne' }}
                        </span>
                        <button @click="toggleStatus"
                            class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                            :class="form.actif ? 'bg-orange-500' : 'bg-gray-400'">
                            <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm"
                                :class="form.actif ? 'translate-x-6' : 'translate-x-1'"></span>
                        </button>
                    </div>
                    <button @click="saveConfig" :disabled="loading"
                        class="bg-black hover:bg-orange-600 text-white px-8 py-3 rounded-lg font-bold flex items-center transition-all active:scale-95 disabled:opacity-50">
                        <i class="fas mr-2" :class="loading ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                        ENREGISTRER
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">

                <!-- Navigation latérale -->
                <div class="lg:col-span-2 space-y-1">
                    <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
                        :class="['w-full flex items-center gap-3 px-4 py-3.5 rounded-xl font-bold text-sm transition-all border-2',
                        activeTab === tab.id ? 'border-black bg-black text-white' : 'border-transparent text-gray-500 hover:bg-gray-100']">
                        <i :class="['fas w-4 text-center', tab.icon]"></i>
                        <span class="truncate">{{ tab.name }}</span>
                    </button>
                </div>

                <!-- Zone de contenu -->
                <div class="lg:col-span-10">

                    <!-- ═══════════════════════════════════════════════════════════ -->
                    <!-- ONGLET PERSONNALISATION : branding + couleurs + mise en page -->
                    <!-- ═══════════════════════════════════════════════════════════ -->
                    <div v-if="activeTab === 'design'" class="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">

                        <!-- Colonne gauche : contrôles -->
                        <div class="space-y-5">

                            <!-- IDENTITÉ VISUELLE -->
                            <div class="bg-white border-2 border-gray-100 rounded-2xl p-6">
                                <h3 class="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">Identité visuelle</h3>
                                <div class="space-y-3">
                                    <div>
                                        <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Nom de l'entreprise</label>
                                        <input v-model="form.branding_company_name" type="text"
                                            class="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none transition-all text-sm">
                                    </div>
                                    <div>
                                        <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Slogan</label>
                                        <input v-model="form.branding_tagline" type="text" placeholder="Ex : Votre confort, notre priorité"
                                            class="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none transition-all text-sm">
                                    </div>
                                    <div>
                                        <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">URL du Logo</label>
                                        <input v-model="form.branding_logo_url" type="text" placeholder="https://..."
                                            class="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none transition-all text-sm">
                                    </div>

                                    <!-- Aperçu logo si présent -->
                                    <div v-if="form.branding_logo_url" class="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border-2 border-gray-100">
                                        <img :src="form.branding_logo_url" class="h-10 object-contain max-w-32 rounded" />
                                        <div>
                                            <p class="text-xs font-bold text-gray-600">Logo actif</p>
                                            <p class="text-xs text-gray-400">L'icône ne sera pas utilisée.</p>
                                        </div>
                                    </div>

                                    <!-- Sélecteur d'icône — uniquement si pas de logo -->
                                    <div v-else>
                                        <label class="block text-xs font-black uppercase text-gray-400 mb-2 tracking-wider">Icône du widget</label>
                                        <div class="grid grid-cols-6 gap-2">
                                            <button v-for="ic in iconOptions" :key="ic.icon"
                                                @click="form.branding_icon = ic.icon"
                                                :title="ic.label"
                                                class="flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all"
                                                :class="form.branding_icon === ic.icon
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-100 bg-gray-50 hover:border-gray-300'">
                                                <i :class="ic.icon" class="text-base"
                                                    :style="{ color: form.branding_icon === ic.icon ? form.color_primary : '#9ca3af' }"></i>
                                                <span class="text-[9px] text-gray-400 leading-none text-center truncate w-full">{{ ic.label }}</span>
                                            </button>
                                        </div>
                                        <p class="text-xs text-gray-400 mt-2 italic">Icône sélectionnée : {{ form.branding_icon }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- COULEURS -->
                            <div class="bg-white border-2 border-gray-100 rounded-2xl p-6">
                                <h3 class="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">Couleurs</h3>
                                <div class="grid grid-cols-2 gap-2.5">
                                    <div v-for="c in colorFields" :key="c.key"
                                        class="flex items-center gap-2 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                                        <input type="color" v-model="form[c.key]"
                                            class="h-8 w-8 rounded-lg cursor-pointer border-0 flex-shrink-0">
                                        <div class="min-w-0 flex-1">
                                            <p class="text-[10px] font-black uppercase text-gray-400 leading-none mb-0.5 truncate">{{ c.label }}</p>
                                            <input v-model="form[c.key]" type="text"
                                                class="text-xs font-mono font-bold w-full bg-transparent outline-none text-gray-600 uppercase">
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- MISE EN PAGE & AFFICHAGE -->
                            <div class="bg-white border-2 border-gray-100 rounded-2xl p-6">
                                <h3 class="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">Mise en page</h3>
                                <div class="space-y-3">
                                    <div class="grid grid-cols-2 gap-3">
                                        <div>
                                            <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Largeur max</label>
                                            <input v-model="form.layout_max_width" type="text" placeholder="540px"
                                                class="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-3 py-2 focus:border-orange-500 outline-none transition-all text-sm">
                                        </div>
                                        <div>
                                            <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Police</label>
                                            <input v-model="form.layout_display_font" type="text" placeholder="inherit"
                                                class="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-3 py-2 focus:border-orange-500 outline-none transition-all text-sm">
                                        </div>
                                    </div>
                                    <div v-for="toggle in allToggles" :key="toggle.key"
                                        class="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-100 gap-3">
                                        <p class="text-sm text-gray-700">{{ toggle.label }}</p>
                                        <button @click="form[toggle.key] = !form[toggle.key]"
                                            class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0"
                                            :class="form[toggle.key] ? 'bg-orange-500' : 'bg-gray-300'">
                                            <span class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                                                :class="form[toggle.key] ? 'translate-x-4' : 'translate-x-0.5'"></span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Colonne droite : PREVIEW LIVE -->
                        <div class="xl:sticky xl:top-6">
                            <p class="text-xs font-black uppercase tracking-widest text-gray-400 mb-3 text-center">Aperçu en temps réel</p>
                            <div class="bg-gray-200 rounded-2xl p-2 border-2 border-gray-300">

                                <!-- Fausse barre navigateur -->
                                <div class="bg-gray-300 rounded-xl px-3 py-2 mb-2 flex items-center gap-2">
                                    <div class="flex gap-1.5 flex-shrink-0">
                                        <div class="w-2 h-2 rounded-full bg-gray-400"></div>
                                        <div class="w-2 h-2 rounded-full bg-gray-400"></div>
                                        <div class="w-2 h-2 rounded-full bg-gray-400"></div>
                                    </div>
                                    <div class="flex-1 bg-white rounded-md px-2 py-0.5 text-xs text-gray-400 font-mono truncate">
                                        /reservation/{{ form.token || '…' }}
                                    </div>
                                </div>

                                <!-- Widget simulé -->
                                <div class="rounded-xl overflow-hidden py-6 px-4 flex flex-col items-center gap-4"
                                    :style="{ background: form.color_background }">

                                    <!-- Header branding -->
                                    <div v-if="form.branding_show_header" class="flex flex-col items-center gap-2 w-full">
                                        <div v-if="form.branding_logo_url" class="flex justify-center">
                                            <img :src="form.branding_logo_url" class="h-10 object-contain max-w-full" />
                                        </div>
                                        <div v-else class="flex items-center gap-2">
                                            <div class="w-9 h-9 rounded-xl flex items-center justify-center text-white"
                                                :style="{ background: form.color_primary }">
                                                <i :class="form.branding_icon || 'fas fa-fire-flame-curved'" class="text-sm"></i>
                                            </div>
                                            <span class="text-base font-black truncate max-w-36"
                                                :style="{ color: form.color_text_primary, fontFamily: form.layout_display_font || 'inherit' }">
                                                {{ form.branding_company_name || 'Mon Entreprise' }}
                                            </span>
                                        </div>
                                        <p v-if="form.branding_tagline" class="text-xs text-center max-w-xs"
                                            :style="{ color: form.color_text_secondary }">
                                            {{ form.branding_tagline }}
                                        </p>
                                    </div>

                                    <!-- Stepper -->
                                    <div v-if="form.layout_show_steps" class="flex items-center w-full max-w-xs gap-1">
                                        <template v-for="(s, i) in previewSteps">
                                            <div :key="'dot-'+i" class="flex flex-col items-center">
                                                <div class="w-6 h-6 rounded-full flex items-center justify-center border-2 font-bold"
                                                    :style="i === 0
                                                        ? { background: form.color_primary_light, borderColor: form.color_primary, color: form.color_primary }
                                                        : { background: form.color_surface, borderColor: form.color_border, color: form.color_text_secondary }">
                                                    <i :class="s.icon" style="font-size:6px;"></i>
                                                </div>
                                            </div>
                                            <div v-if="i < previewSteps.length - 1" :key="'line-'+i"
                                                class="flex-1 h-0.5 rounded-full"
                                                :style="{ background: form.color_border }"></div>
                                        </template>
                                    </div>

                                    <!-- Card principale -->
                                    <div class="w-full max-w-xs overflow-hidden"
                                        :style="{
                                            background:   form.color_surface,
                                            border:       `1px solid ${form.color_border}`,
                                            borderRadius: form.layout_card_rounded ? '20px' : '8px',
                                            boxShadow:    form.layout_card_shadow ? '0 8px 32px rgba(0,0,0,0.09)' : 'none'
                                        }">

                                        <!-- Card header -->
                                        <div class="px-4 pt-4 pb-3">
                                            <div class="flex items-center gap-2.5">
                                                <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                                                    :style="{ background: form.color_primary_light }">
                                                    <i class="fas fa-fire text-sm" :style="{ color: form.color_primary }"></i>
                                                </div>
                                                <div class="min-w-0">
                                                    <p class="text-sm font-black leading-tight"
                                                        :style="{ color: form.color_text_primary, fontFamily: form.layout_display_font || 'inherit' }">
                                                        Votre appareil
                                                    </p>
                                                    <p class="text-xs truncate" :style="{ color: form.color_text_secondary }">
                                                        Sélectionnez un équipement
                                                    </p>
                                                </div>
                                                <span class="ml-auto text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                                                    :style="{ background: form.color_primary_light, color: form.color_primary }">
                                                    1/5
                                                </span>
                                            </div>
                                        </div>
                                        <div class="mx-4 h-px" :style="{ background: form.color_border }"></div>

                                        <!-- Devices fictifs -->
                                        <div class="px-4 py-3 grid grid-cols-3 gap-2">
                                            <div v-for="(d, i) in previewDevices" :key="i"
                                                class="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2"
                                                :style="i === 0
                                                    ? { borderColor: form.color_primary, background: form.color_primary_light }
                                                    : { borderColor: form.color_border, background: 'transparent' }">
                                                <i :class="d.icon" class="text-base"
                                                    :style="{ color: i === 0 ? form.color_primary : form.color_text_secondary }"></i>
                                                <span class="text-xs font-bold text-center leading-tight"
                                                    :style="{ color: i === 0 ? form.color_primary : form.color_text_primary }">
                                                    {{ d.label }}
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Bouton CTA -->
                                        <div class="px-4 pb-4">
                                            <div class="w-full py-2.5 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2"
                                                :style="{ background: form.color_primary }">
                                                {{ form.label_next || 'Continuer' }}
                                                <i class="fas fa-arrow-right text-xs"></i>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Powered by -->
                                    <p v-if="form.branding_powered_by" class="text-xs opacity-30"
                                        :style="{ color: form.color_text_secondary }">
                                        Propulsé par Termoflow
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ══════════════════════════════════════════ -->
                    <!-- ONGLET : TEXTES & LABELS                  -->
                    <!-- ══════════════════════════════════════════ -->
                    <div v-if="activeTab === 'labels'" class="bg-white border-2 border-gray-100 rounded-2xl p-8 space-y-10">
                        <section>
                            <h3 class="text-base font-black mb-6 border-b-2 border-orange-500 pb-2 inline-block tracking-tight">LIBELLÉS DES CHAMPS</h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Champ Nom</label>
                                    <input v-model="form.label_nom" type="text" placeholder="Nom" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Champ Prénom</label>
                                    <input v-model="form.label_prenom" type="text" placeholder="Prénom" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Champ Téléphone</label>
                                    <input v-model="form.label_telephone" type="text" placeholder="Téléphone" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Champ Email</label>
                                    <input v-model="form.label_email" type="text" placeholder="Email" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Notes / Problème</label>
                                    <input v-model="form.label_notes" type="text" placeholder="Décrivez votre problème" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Recherche d'adresse</label>
                                    <input v-model="form.label_search_address" type="text" placeholder="Rechercher votre adresse" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                            </div>
                        </section>

                        <section>
                            <h3 class="text-base font-black mb-6 border-b-2 border-orange-500 pb-2 inline-block tracking-tight">BOUTONS & NAVIGATION</h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Bouton principal (CTA)</label>
                                    <input v-model="form.label_cta" type="text" placeholder="Confirmer le rendez-vous" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Bouton Suivant</label>
                                    <input v-model="form.label_next" type="text" placeholder="Continuer" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                            </div>
                        </section>

                        <section>
                            <h3 class="text-base font-black mb-6 border-b-2 border-orange-500 pb-2 inline-block tracking-tight">MESSAGES DE CONFIRMATION</h3>
                            <div class="space-y-5">
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Titre succès</label>
                                    <input v-model="form.label_success_title" type="text" placeholder="Demande envoyée !" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Message succès</label>
                                    <textarea v-model="form.label_success_message" rows="2"
                                        placeholder="Nous vous contacterons rapidement…"
                                        class="w-full border-2 border-gray-100 rounded-xl px-4 py-3 focus:border-orange-500 outline-none transition-all text-sm resize-none"></textarea>
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Message de réassurance</label>
                                    <input v-model="form.label_contact_reassurance" type="text"
                                        placeholder="Vos coordonnées ne seront partagées qu'avec l'artisan."
                                        class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                            </div>
                        </section>

                        <section>
                            <h3 class="text-base font-black mb-6 border-b-2 border-orange-500 pb-2 inline-block tracking-tight">PLACEHOLDERS</h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Nom</label>
                                    <input v-model="form.placeholder_nom" type="text" placeholder="Dupont" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Prénom</label>
                                    <input v-model="form.placeholder_prenom" type="text" placeholder="Jean" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Téléphone</label>
                                    <input v-model="form.placeholder_telephone" type="text" placeholder="06 12 34 56 78" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Email</label>
                                    <input v-model="form.placeholder_email" type="text" placeholder="jean@mail.com" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Notes</label>
                                    <input v-model="form.placeholder_notes" type="text" placeholder="Ex : ma chaudière fait du bruit…" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                                <div class="md:col-span-2">
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">Adresse</label>
                                    <input v-model="form.placeholder_address" type="text" placeholder="12 rue des Fleurs, Lyon…" class="w-full border-b-2 border-gray-100 py-2 focus:border-orange-500 outline-none transition-all text-sm font-medium bg-transparent">
                                </div>
                            </div>
                        </section>
                    </div>

                    <!-- ══════════════════════════════════════════ -->
                    <!-- ONGLET : CONFIDENTIALITÉ                  -->
                    <!-- ══════════════════════════════════════════ -->
                    <div v-if="activeTab === 'privacy'" class="bg-white border-2 border-gray-100 rounded-2xl p-8 space-y-6">
                        <h3 class="text-base font-black border-b-2 border-orange-500 pb-2 inline-block tracking-tight">CONFIDENTIALITÉ</h3>
                        <div class="flex items-center justify-between px-4 py-3.5 bg-gray-50 rounded-xl border-2 border-gray-100 gap-4">
                            <div>
                                <p class="font-bold text-sm">Afficher la mention de confidentialité</p>
                                <p class="text-xs text-gray-400 mt-0.5">Ajoute un encart informatif sous le formulaire client.</p>
                            </div>
                            <button @click="form.privacy_show = !form.privacy_show"
                                class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0"
                                :class="form.privacy_show ? 'bg-orange-500' : 'bg-gray-300'">
                                <span class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm"
                                    :class="form.privacy_show ? 'translate-x-6' : 'translate-x-1'"></span>
                            </button>
                        </div>
                        <div v-if="form.privacy_show">
                            <label class="block text-xs font-black uppercase text-gray-400 mb-2 tracking-wider">Texte de confidentialité</label>
                            <textarea v-model="form.privacy_text" rows="4"
                                placeholder="Vos données sont transmises uniquement à l'artisan et ne sont pas revendues."
                                class="w-full border-2 border-gray-100 rounded-xl px-4 py-3 focus:border-orange-500 outline-none transition-all text-sm resize-none"></textarea>
                        </div>
                        <div v-if="form.privacy_show && form.privacy_text" class="bg-gray-50 rounded-xl p-4 border-l-4 border-orange-400">
                            <p class="text-xs text-gray-500 font-black uppercase mb-1">Aperçu :</p>
                            <p class="text-sm text-gray-600">{{ form.privacy_text }}</p>
                        </div>
                    </div>

                    <!-- ══════════════════════════════════════════ -->
                    <!-- ONGLET : INTÉGRATION                      -->
                    <!-- ══════════════════════════════════════════ -->
                    <div v-if="activeTab === 'integration'" class="bg-white border-2 border-gray-100 rounded-2xl p-8 space-y-8">
                        <section>
                            <h3 class="text-base font-black mb-6 border-b-2 border-orange-500 pb-2 inline-block tracking-tight">ACCÈS PUBLIC</h3>
                            <div class="bg-black text-white p-6 rounded-2xl">
                                <div class="flex justify-between items-center mb-4">
                                    <span class="text-xs font-bold uppercase tracking-widest text-gray-400">Lien public de réservation</span>
                                    <i class="fas fa-link text-orange-500"></i>
                                </div>
                                <div class="flex gap-3">
                                    <input readonly :value="publicUrl" class="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-sm font-mono">
                                    <button @click="copyLink" class="bg-orange-500 hover:bg-orange-600 text-white px-4 rounded-lg transition-colors">
                                        <i class="fas fa-copy"></i>
                                    </button>
                                </div>
                                <p class="text-xs text-gray-500 mt-3 font-mono">{{ form.token }}</p>
                            </div>
                        </section>

                        <section>
                            <h3 class="text-base font-black mb-6 border-b-2 border-orange-500 pb-2 inline-block tracking-tight">WEBHOOK</h3>
                            <div class="space-y-4">
                                <div>
                                    <label class="block text-xs font-black uppercase text-gray-400 mb-1 tracking-wider">URL de rappel</label>
                                    <input v-model="form.webhook_url" type="url" placeholder="https://votre-site.com/api/callback"
                                        class="w-full bg-gray-50 border-2 border-gray-100 rounded-xl px-4 py-3 focus:border-orange-500 outline-none transition-all text-sm">
                                    <p class="text-xs text-gray-400 mt-2 italic">Les données de chaque réservation sont envoyées en POST à cette adresse.</p>
                                </div>
                                <div v-if="form.webhook_url">
                                    <button @click="testWebhook" :disabled="testingWebhook"
                                        class="border-2 border-black text-black px-6 py-2 rounded-xl text-sm font-bold hover:bg-black hover:text-white transition-all disabled:opacity-50">
                                        <i class="fas mr-2" :class="testingWebhook ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
                                        {{ testingWebhook ? 'Envoi...' : 'Tester le webhook' }}
                                    </button>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h3 class="text-base font-black mb-6 border-b-2 border-orange-500 pb-2 inline-block tracking-tight">STATISTIQUES</h3>
                            <div class="grid grid-cols-2 gap-4">
                                <div class="bg-gray-50 rounded-2xl p-5 text-center border-2 border-gray-100">
                                    <p class="text-4xl font-black text-black">{{ form.views_count || 0 }}</p>
                                    <p class="text-xs font-bold uppercase text-gray-400 mt-1">Vues</p>
                                </div>
                                <div class="bg-orange-50 rounded-2xl p-5 text-center border-2 border-orange-100">
                                    <p class="text-4xl font-black text-orange-500">{{ form.submissions_count || 0 }}</p>
                                    <p class="text-xs font-bold uppercase text-gray-400 mt-1">Réservations</p>
                                </div>
                            </div>
                            <p class="text-xs text-gray-400 mt-3 italic text-center">
                                Taux de conversion :
                                <strong class="text-black">
                                    {{ form.views_count > 0 ? Math.round((form.submissions_count / form.views_count) * 100) : 0 }}%
                                </strong>
                            </p>
                        </section>
                    </div>

                </div>
            </div>
        </div>

        <!-- Loader -->
        <div v-else class="flex flex-col items-center justify-center min-h-96">
            <div class="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            <p class="text-xs font-black uppercase tracking-widest text-gray-400 mt-4">Initialisation...</p>
        </div>
    </app-template>
</template>

<script>
export default {
    name: 'ParametreReservation',
    data() {
        return {
            loading:       false,
            fetching:      true,
            testingWebhook: false,
            activeTab:     'design',

            tabs: [
                { id: 'design',      name: 'Personnalisation', icon: 'fa-palette'       },
                { id: 'labels',      name: 'Textes',           icon: 'fa-font'          },
                { id: 'privacy',     name: 'Confidentialité',  icon: 'fa-shield-halved' },
                { id: 'integration', name: 'Intégration',      icon: 'fa-code'          },
            ],

            colorFields: [
                { key: 'color_primary',        label: 'Principale'        },
                { key: 'color_primary_light',  label: 'Principale claire' },
                { key: 'color_background',     label: 'Fond'              },
                { key: 'color_surface',        label: 'Surface'           },
                { key: 'color_text_primary',   label: 'Texte'             },
                { key: 'color_text_secondary', label: 'Texte secondaire'  },
                { key: 'color_border',         label: 'Bordures'          },
                { key: 'color_success',        label: 'Succès'            },
                { key: 'color_error',          label: 'Erreur'            },
            ],

            // Tous les toggles regroupés (layout + affichage)
            allToggles: [
                { key: 'layout_show_steps',          label: 'Barre de progression' },
                { key: 'layout_card_shadow',         label: 'Ombre sur les cartes' },
                { key: 'layout_card_rounded',        label: 'Coins arrondis'       },
                { key: 'layout_show_restart_button', label: 'Bouton "Recommencer"' },
                { key: 'branding_show_header',       label: "Afficher l'en-tête"  },
                { key: 'branding_powered_by',        label: '"Propulsé par"'      },
            ],

            // Icônes Font Awesome thème chauffage / climatisation
            iconOptions: [
                { icon: 'fas fa-fire-flame-curved', label: 'Flamme'      },
                { icon: 'fas fa-fire',              label: 'Feu'         },
                { icon: 'fas fa-snowflake',         label: 'Froid'       },
                { icon: 'fas fa-thermometer-half',  label: 'Thermomètre' },
                { icon: 'fas fa-wind',              label: 'Vent'        },
                { icon: 'fas fa-fan',               label: 'Ventilateur' },
                { icon: 'fas fa-droplet',           label: 'Eau'         },
                { icon: 'fas fa-house',             label: 'Maison'      },
                { icon: 'fas fa-bolt',              label: 'Énergie'     },
                { icon: 'fas fa-sun',               label: 'Soleil'      },
                { icon: 'fas fa-leaf',              label: 'Écologie'    },
                { icon: 'fas fa-plug',              label: 'Électrique'  },
                { icon: 'fas fa-wrench',            label: 'Dépannage'   },
                { icon: 'fas fa-screwdriver-wrench',label: 'Outillage'   },
                { icon: 'fas fa-gear',              label: 'Mécanique'   },
                { icon: 'fas fa-shield-halved',     label: 'Garantie'    },
                { icon: 'fas fa-calendar-check',    label: 'Entretien'   },
                { icon: 'fas fa-star',              label: 'Qualité'     },
            ],

            // Preview fictive
            previewDevices: [
                { label: 'Chaudière',   icon: 'fas fa-fire'      },
                { label: 'Climatisation', icon: 'fas fa-snowflake' },
                { label: 'PAC',         icon: 'fas fa-wind'      },
            ],
            previewSteps: [
                { icon: 'fas fa-fire'           },
                { icon: 'fas fa-wrench'         },
                { icon: 'fas fa-user'           },
                { icon: 'fas fa-map-marker-alt' },
                { icon: 'fas fa-clipboard-list' },
            ],

            form: {
                id: null, token: '', actif: false,

                // Branding
                branding_logo_url: '', branding_company_name: '', branding_tagline: '',
                branding_icon: 'fas fa-fire-flame-curved',
                branding_show_header: true, branding_powered_by: true,

                // Colors
                color_primary: '#3B82F6', color_primary_light: '#edf0f7', color_primary_dark: '#c53030',
                color_background: '#F9FAFB', color_surface: '#ffffff',
                color_text_primary: '#111827', color_text_secondary: '#718096',
                color_success: '#38a169', color_success_light: '#f0fff4',
                color_border: '#e8ecf0', color_error: '#e53e3e',

                // Layout
                layout_max_width: '540px', layout_show_steps: true, layout_card_shadow: true,
                layout_card_rounded: true, layout_display_font: null, layout_show_restart_button: true,

                // Labels
                label_nom: 'Nom', label_prenom: 'Prénom', label_telephone: 'Téléphone', label_email: 'Email',
                label_notes: 'Décrivez votre problème (optionnel)',
                label_search_address: 'Rechercher votre adresse',
                label_contact_reassurance: "Vos coordonnées ne seront partagées qu'avec l'artisan.",
                label_success_title: 'Demande envoyée !',
                label_success_message: 'Nous vous contacterons rapidement pour confirmer votre rendez-vous.',
                label_cta: 'Confirmer le rendez-vous', label_next: 'Continuer',

                // Placeholders
                placeholder_nom: 'Dupont', placeholder_prenom: 'Jean',
                placeholder_telephone: '06 12 34 56 78', placeholder_email: 'jean@mail.com',
                placeholder_notes: 'Ex : ma chaudière fait du bruit depuis 3 jours…',
                placeholder_address: '12 rue des Fleurs, Lyon…',

                // Privacy
                privacy_show: true,
                privacy_text: "Vos données sont transmises uniquement à l'artisan et ne sont pas revendues.",

                // Integration
                webhook_url: '',

                // Stats (read-only)
                views_count: 0, submissions_count: 0,
            }
        }
    },

    computed: {
        publicUrl() {
            return `${window.location.origin}/reservation/${this.form.token}`;
        }
    },

    mounted() { this.fetchConfig(); },

    methods: {
        async fetchConfig() {
            this.fetching = true;
            try {
                const res = await this.$request.post('reservation-configs/me');
                if (res) this.syncData(res);
            } catch (e) {
                this.$toast.error("Erreur de chargement");
            } finally {
                this.fetching = false;
            }
        },

        syncData(rawData) {
            // L'API renvoie { status, api, error } — on déballе api si présent
            const data = rawData.api || rawData;

            this.form.id                = data.id;
            this.form.token             = data.token;
            this.form.actif             = !!data.actif;
            this.form.webhook_url       = data.webhookUrl || '';
            // Supporte { stats: { views, submissions } } et l'ancien format
            this.form.views_count       = data.stats?.views       ?? data.viewsCount       ?? 0;
            this.form.submissions_count = data.stats?.submissions  ?? data.submissionsCount ?? 0;

            if (data.branding) {
                // L'API renvoie du snake_case pour branding
                this.form.branding_logo_url     = data.branding.logo_url     || '';
                this.form.branding_company_name = data.branding.company_name || '';
                this.form.branding_tagline      = data.branding.tagline      || '';
                this.form.branding_icon         = data.branding.icon         || 'fas fa-fire-flame-curved';
                this.form.branding_show_header  = !!data.branding.show_header;
                this.form.branding_powered_by   = !!data.branding.powered_by;
            }

            if (data.colors) {
                this.form.color_primary        = data.colors.primary        || '#3B82F6';
                this.form.color_primary_light  = data.colors.primaryLight   || '#edf0f7';
                this.form.color_primary_dark   = data.colors.primaryDark    || '#c53030';
                this.form.color_background     = data.colors.background     || '#F9FAFB';
                this.form.color_surface        = data.colors.surface        || '#ffffff';
                this.form.color_text_primary   = data.colors.textPrimary    || '#111827';
                this.form.color_text_secondary = data.colors.textSecondary  || '#718096';
                this.form.color_success        = data.colors.success        || '#38a169';
                this.form.color_success_light  = data.colors.successLight   || '#f0fff4';
                this.form.color_border         = data.colors.border         || '#e8ecf0';
                this.form.color_error          = data.colors.error          || '#e53e3e';
            }

            if (data.layout) {
                this.form.layout_max_width           = data.layout.maxWidth           || '540px';
                this.form.layout_show_steps          = !!data.layout.showSteps;
                this.form.layout_card_shadow         = !!data.layout.cardShadow;
                this.form.layout_card_rounded        = !!data.layout.cardRounded;
                this.form.layout_display_font        = data.layout.displayFont        || null;
                this.form.layout_show_restart_button = !!data.layout.showRestartButton;
            }

            if (data.labels) {
                const map = {
                    label_nom: 'nom', label_prenom: 'prenom', label_telephone: 'telephone',
                    label_email: 'email', label_notes: 'notes', label_search_address: 'searchAddress',
                    label_contact_reassurance: 'contactReassurance', label_success_title: 'successTitle',
                    label_success_message: 'successMessage', label_cta: 'cta', label_next: 'next',
                };
                Object.entries(map).forEach(([k, v]) => { this.form[k] = data.labels[v] || ''; });
            }

            if (data.placeholders) {
                const map = {
                    placeholder_nom: 'nom', placeholder_prenom: 'prenom',
                    placeholder_telephone: 'telephone', placeholder_email: 'email',
                    placeholder_notes: 'notes', placeholder_address: 'address',
                };
                Object.entries(map).forEach(([k, v]) => { this.form[k] = data.placeholders[v] || ''; });
            }

            if (data.privacy) {
                this.form.privacy_show = !!data.privacy.show;
                this.form.privacy_text = data.privacy.text || '';
            }
        },

        async saveConfig() {
            this.loading = true;
            try {
                const res = await this.$request.put(`reservation-configs/${this.form.id}`, this.form);
                if (res) { this.$toast.success("Mise à jour réussie"); this.syncData(res); }
            } catch (e) {
                this.$toast.error("Erreur lors de l'enregistrement");
            } finally {
                this.loading = false;
            }
        },

        async toggleStatus() {
            try {
                const res = await this.$request.post(`reservation-configs/${this.form.id}/toggle`);
                this.form.actif = res.actif;
                this.$toast.info(res.actif ? "Formulaire en ligne" : "Formulaire hors ligne");
            } catch (e) {
                this.$toast.error("Erreur de statut");
            }
        },

        async testWebhook() {
            this.testingWebhook = true;
            try {
                await this.$request.post(`reservation-configs/${this.form.id}/test-webhook`);
                this.$toast.success("Webhook testé avec succès !");
            } catch (e) {
                this.$toast.error("Échec du test webhook");
            } finally {
                this.testingWebhook = false;
            }
        },

        copyLink() {
            navigator.clipboard.writeText(this.publicUrl);
            this.$toast.success("Lien copié !");
        },
    }
}
</script>